import { useTranslation } from '@/lib/i18n';
import type {
  VendorApplicationAdminDetail,
  VendorKycDocumentRef,
} from '@/lib/api-admin/types';

type DownloadEntry = NonNullable<
  VendorApplicationAdminDetail['kycDocumentDownloadUrls']
>[number];

export interface VendorKycDocumentListProps {
  documents: ReadonlyArray<VendorKycDocumentRef>;
  downloadUrls?: ReadonlyArray<DownloadEntry> | null | undefined;
}

const formatBytes = (size: number | undefined): string => {
  if (!size || size <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let val = size;
  let i = 0;
  while (val >= 1024 && i < units.length - 1) {
    val /= 1024;
    i++;
  }
  return `${val.toFixed(val < 10 ? 1 : 0)} ${units[i]}`;
};

const formatExpiresIn = (iso: string | undefined): string => {
  if (!iso) return '';
  const expires = new Date(iso).getTime();
  if (Number.isNaN(expires)) return '';
  const diff = Math.max(0, expires - Date.now());
  const minutes = Math.floor(diff / 60000);
  return `${minutes}m`;
};

export const VendorKycDocumentList = ({
  documents,
  downloadUrls,
}: VendorKycDocumentListProps) => {
  const { t } = useTranslation('vendors');

  if (documents.length === 0) {
    return (
      <p className="text-sm text-[color:var(--content-muted)]">
        {t('vendors.kyc.empty')}
      </p>
    );
  }

  const urlIndex = new Map<string, DownloadEntry>();
  if (downloadUrls) {
    for (const entry of downloadUrls) urlIndex.set(entry.documentId, entry);
  }

  return (
    <ul aria-label={t('vendors.kyc.label')} className="space-y-2">
      {documents.map((doc) => {
        const url = urlIndex.get(doc.documentId);
        const expiresIn = url ? formatExpiresIn(url.expiresAt) : '';
        return (
          <li
            key={doc.documentId}
            className="flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2"
            style={{ borderColor: 'var(--content-muted)' }}
          >
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-medium text-[color:var(--content-strong)]">
                {t(`vendors.kyc.documentType.${doc.documentType}`, { defaultValue: '-' })}
              </span>
              <span className="text-xs text-[color:var(--content-muted)]">
                {doc.fileName ?? '—'} · {formatBytes(doc.fileSize)}
              </span>
            </div>
            {url ? (
              <a
                href={url.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-8 items-center rounded-md border px-3 text-sm focus-visible:outline-none focus-visible:ring-2"
                style={{
                  borderColor: 'var(--content-muted)',
                  color: 'var(--content-strong)',
                }}
              >
                {t('vendors.kyc.download')}
                {expiresIn ? (
                  <span className="ml-2 text-xs text-[color:var(--content-muted)]">
                    ({t('vendors.kyc.expiresIn', { time: expiresIn })})
                  </span>
                ) : null}
              </a>
            ) : (
              <span className="text-xs text-[color:var(--content-muted)]">
                {t('vendors.kyc.noDownload')}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
};
